import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, Layout, Menu, Typography, type MenuProps } from 'antd';
import { AppstoreOutlined, LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { useLogout } from '@presentation/hooks/useAuth';
import { useAuthStore } from '@presentation/stores/authStore';
import { WorkspaceSwitcher } from '@presentation/components/workspace/WorkspaceSwitcher';
import { NotificationBell } from '@presentation/components/layout/NotificationBell';
import { APP_NAME } from '@shared/constants';
import { paths } from '@app/routes/paths';

const NAV_ITEMS: MenuProps['items'] = [
  {
    key: paths.dashboard,
    label: <Link to={paths.dashboard}>Dashboard</Link>,
    icon: <AppstoreOutlined />,
  },
  { key: paths.workspaces, label: <Link to={paths.workspaces}>Workspaces</Link> },
];

export const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useLogout();

  const selectedKey =
    NAV_ITEMS?.map((item) => item?.key as string).find((key) =>
      location.pathname.startsWith(key),
    ) ?? paths.dashboard;

  // Dashboard/Workspaces are duplicated here (alongside the horizontal Menu,
  // which is hidden below `sm`) so navigation stays reachable on narrow
  // viewports without racing the Menu's own overflow-collapse calculation
  // against its shrinking siblings.
  const userMenuItems: MenuProps['items'] = [
    {
      key: paths.dashboard,
      label: 'Dashboard',
      icon: <AppstoreOutlined />,
      className: 'sm:hidden',
    },
    { key: paths.workspaces, label: 'Workspaces', className: 'sm:hidden' },
    { type: 'divider', className: 'sm:hidden' },
    { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
    { type: 'divider' },
    { key: 'logout', label: 'Sign out', icon: <LogoutOutlined />, danger: true },
  ];

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'settings') {
      navigate(paths.settings);
      return;
    }
    if (key === 'logout') {
      logout.mutate();
      return;
    }
    if (key === paths.dashboard || key === paths.workspaces) {
      navigate(key);
    }
  };

  return (
    <Layout className="min-h-screen">
      <Layout.Header className="!flex !h-14 items-center !px-4">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <Link to={paths.dashboard} className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            <img src="/apilab-icon-64.png" alt="" width={24} height={24} />
            <Typography.Title level={5} className="!m-0 !text-inherit">
              {APP_NAME}
            </Typography.Title>
          </Link>
          <div className="shrink-0">
            <WorkspaceSwitcher />
          </div>
          <Menu
            mode="horizontal"
            items={NAV_ITEMS}
            selectedKeys={[selectedKey]}
            className="hidden min-w-0 flex-1 !border-none !bg-transparent sm:!flex"
          />
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <NotificationBell />
          <Dropdown
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            trigger={['click']}
          >
            <Button type="text" className="!flex !items-center !gap-2">
              <Avatar size="small" src={user?.avatarUrl} icon={<UserOutlined />} />
              <span className="hidden max-w-[30vw] truncate sm:inline">{user?.email}</span>
            </Button>
          </Dropdown>
        </div>
      </Layout.Header>
      <Layout.Content>
        <Outlet />
      </Layout.Content>
    </Layout>
  );
};
